import { signOutWithNotifications } from "../../firebase/notificationSession";
// components/auth/verify-email.component.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";

import { auth } from "../../firebase/firebase.utils";
import { getVerificationActionSettings } from "../../firebase/auth-profile.utils";
import RetroButton from "../retro/button/retro-button.component";

import "./auth-gate.styles.scss";

const RESEND_COOLDOWN_SECONDS = 30;

const getInitialCooldown = () => {
  const sentAt = Number(sessionStorage.getItem("verificationEmailSentAt"));
  if (!Number.isFinite(sentAt)) return 0;

  return Math.max(
    0,
    Math.ceil(
      (sentAt + RESEND_COOLDOWN_SECONDS * 1000 - Date.now()) / 1000,
    ),
  );
};

const VerifyEmail = ({ onVerified }) => {
  const [cooldown, setCooldown] = useState(getInitialCooldown);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const checkInFlight = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;

    const timer = window.setInterval(() => {
      setCooldown((current) => Math.max(0, current - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [cooldown > 0]);

  const checkVerification = useCallback(
    async ({ silent = false } = {}) => {
      if (checkInFlight.current) return;

      const currentUser = auth.currentUser;
      if (!currentUser) {
        setErrorMessage("You have been signed out. Please log in again.");
        return;
      }

      checkInFlight.current = true;
      if (!silent) setBusy(true);
      setErrorMessage("");
      if (!silent) setMessage("");

      try {
        await currentUser.reload();
        const refreshedUser = auth.currentUser;

        if (!refreshedUser?.emailVerified) {
          if (!silent) {
            setErrorMessage(
              "We still can’t confirm verification. Open the link in your email, then try again.",
            );
          }
          return;
        }

        setMessage("Email verified. Finishing your account setup…");
        // App owns the one-time token refresh, public verification mirror, and
        // birthday gate. Passing the refreshed user avoids another reload.
        await onVerified?.(refreshedUser);
      } catch (error) {
        console.error("Verification refresh failed:", error);
        if (!silent) {
          setErrorMessage(
            "We could not refresh your verification status. Please try again.",
          );
        }
      } finally {
        checkInFlight.current = false;
        if (!silent) setBusy(false);
      }
    },
    [onVerified],
  );

  useEffect(() => {
    const checkWhenVisible = () => {
      if (document.visibilityState === "visible") {
        void checkVerification({ silent: true });
      }
    };

    window.addEventListener("focus", checkWhenVisible);
    document.addEventListener("visibilitychange", checkWhenVisible);

    return () => {
      window.removeEventListener("focus", checkWhenVisible);
      document.removeEventListener("visibilitychange", checkWhenVisible);
    };
  }, [checkVerification]);

  const resendVerification = async () => {
    if (cooldown > 0 || busy) return;

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setErrorMessage("You have been signed out. Please log in again.");
      return;
    }

    try {
      setBusy(true);
      setErrorMessage("");
      setMessage("");

      await currentUser.sendEmailVerification(
        getVerificationActionSettings(),
      );
      sessionStorage.setItem("verificationEmailSentAt", String(Date.now()));
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setMessage("Verification email sent. Check your inbox and spam folder.");
    } catch (error) {
      console.error("Verification resend failed:", error);
      setErrorMessage(
        error?.code === "auth/too-many-requests"
          ? "Too many attempts. Please wait a little longer before trying again."
          : "We could not send the verification email. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  const email = auth.currentUser?.email || "your email address";

  return (
    <main className="authGate">
      <section className="authGate__card">
        <img
          className="authGate__logo"
          src="/antlogy_logo.png"
          alt="Antlogy"
        />
        <h1>Verify your email</h1>
        <p>We sent a verification link to:</p>
        <p className="authGate__email">{email}</p>
        <p>
          Open the link, then return here. This page also checks automatically
          when you come back to the tab.
        </p>

        <RetroButton
          onClick={() => checkVerification()}
          disabled={busy}
          style={{ width: "100%" }}
        >
          {busy ? "Checking…" : "I’ve verified"}
        </RetroButton>

        <button
          className="authGate__secondary"
          type="button"
          disabled={busy || cooldown > 0}
          onClick={resendVerification}
        >
          {cooldown > 0
            ? `Resend verification email (${cooldown}s)`
            : "Resend verification email"}
        </button>

        <button
          className="authGate__link"
          type="button"
          disabled={busy}
          onClick={() => signOutWithNotifications()}
        >
          Sign out
        </button>

        {message && (
          <p className="authGate__success" role="status">
            {message}
          </p>
        )}
        {errorMessage && (
          <p className="authGate__error" role="alert">
            {errorMessage}
          </p>
        )}

        <p className="authGate__tip">
          Tip: check spam or junk folders. If you use an email alias, check the
          mailbox it forwards to.
        </p>
      </section>
    </main>
  );
};

export default VerifyEmail;
